// app/bulk-edit.ts — сводка общих значений выбранных строк и слияние правок для массового сохранения.

import { getRowValue } from "./row-value";

export const BULK_EDIT_SUMMARY_KIND = ["single", "mixed"] as const;
export const BULK_EDIT_SUMMARY_KIND_OBJ = {
  SINGLE: BULK_EDIT_SUMMARY_KIND[0],
  MIXED: BULK_EDIT_SUMMARY_KIND[1],
} as const;

export type BulkEditSummary =
  | { kind: typeof BULK_EDIT_SUMMARY_KIND_OBJ.SINGLE; value: unknown }
  | { kind: typeof BULK_EDIT_SUMMARY_KIND_OBJ.MIXED };

/**
 * Summary of effective values across selected rows, per editable column.
 * Used to seed the "apply-to-all" row inputs: "single" when every selected
 * row shares the same value (individual edit ?? original), "mixed" otherwise.
 */
export function summarizeBulkRows<TData>(params: {
  selectedRowIds: ReadonlySet<string>;
  data: readonly TData[];
  getRowId: (row: TData) => string;
  bulkEdits: ReadonlyMap<string, Partial<TData>>;
  editableColumns: readonly string[];
}): Record<string, BulkEditSummary> {
  const { selectedRowIds, data, getRowId, bulkEdits, editableColumns } = params;
  const result: Record<string, BulkEditSummary> = {};
  if (selectedRowIds.size === 0) return result;
  const dataById = new Map(data.map((r) => [getRowId(r), r]));
  for (const colId of editableColumns) {
    let first: unknown = undefined;
    let allSame = true;
    let started = false;
    for (const rowId of selectedRowIds) {
      const row = dataById.get(rowId);
      const individual = getRowValue(bulkEdits.get(rowId), colId);
      const v = individual ?? getRowValue(row, colId);
      if (!started) {
        first = v;
        started = true;
      } else if (v !== first) {
        allSame = false;
      }
    }
    result[colId] = allSame
      ? { kind: BULK_EDIT_SUMMARY_KIND_OBJ.SINGLE, value: first }
      : { kind: BULK_EDIT_SUMMARY_KIND_OBJ.MIXED };
  }
  return result;
}

/**
 * Общие значения применяются к каждой выбранной строке, индивидуальная правка колонки
 * перекрывает общее значение; строка без правок в результат не попадает.
 */
export function mergeBulkEdits<TData>(
  selectedRowIds: Iterable<string>,
  bulkEdits: ReadonlyMap<string, Partial<TData>>,
  commonRowValues: Partial<Record<string, unknown>>,
): Map<string, Partial<TData>> {
  const selectedEdits = new Map<string, Partial<TData>>();
  const hasCommon = Object.keys(commonRowValues).length > 0;
  for (const rowId of selectedRowIds) {
    const individual = bulkEdits.get(rowId) ?? {};
    const merged: Partial<TData> = Object.assign<
      Partial<TData>,
      Partial<Record<string, unknown>>,
      Partial<TData>
    >({}, hasCommon ? commonRowValues : {}, individual);
    if (Object.keys(merged).length > 0) selectedEdits.set(rowId, merged);
  }
  return selectedEdits;
}
